import React from 'react';
import { IconX } from '@veneer/core';
import { PrinterItem, PrinterSpecs } from 'src/utils/Products/productFilter';
import { LABELS, PrinterType, PrinterTypeKey } from 'src/utils/features';
import { FeatureContainer, FeatureItem } from './styles';

export interface PrinterSpecification {
  printerItem: PrinterItem;
}

export const PrinterSpecification: React.FC<PrinterSpecification> = ({
  printerItem
}) => {
  // Parse specifications with error handling
  const getSpecifications = (): PrinterSpecs => {
    try {
      return JSON.parse(printerItem?.specificationList || '{}');
    } catch (error) {
      console.warn('Failed to parse printer specifications:', error);
      return {} as PrinterSpecs;
    }
  };

  // Render dimension feature
  const renderDimensions = (value: {
    width: string;
    depth: string;
    height: string;
    unit: string;
  }) => (
    <FeatureItem>
      <div>{`${value.width} x ${value.depth} x ${value.height} ${value.unit}`}</div>
      <div>{'(W x D x H)'}</div>
    </FeatureItem>
  );

  // Render boolean feature
  const renderBoolean = (key: string, value: boolean) => (
    <FeatureItem>
      {value ? (
        <div>{LABELS[key as keyof typeof LABELS] || key}</div>
      ) : (
        <IconX
          size={28}
          color={'#1A1A1A'}
        />
      )}
    </FeatureItem>
  );

  // Render string feature
  const renderString = (key: string, printerSpecs: PrinterSpecs) => (
    <FeatureItem>
      <div>{PrinterType[printerSpecs.inkType as PrinterTypeKey] || key}</div>
    </FeatureItem>
  );

  // Render number feature
  const renderNumber = (key: string, value: number) => (
    <FeatureItem>
      <div>
        <strong>{value}</strong>
      </div>
      <div>{LABELS[key as keyof typeof LABELS] || key}</div>
    </FeatureItem>
  );

  // Render specification item based on type
  const renderSpecificationItem = (
    key: string,
    value: unknown,
    specs: PrinterSpecs
  ) => {
    if (key === 'dimensions' && typeof value === 'object') {
      return renderDimensions(
        value as {
          width: string;
          depth: string;
          height: string;
          unit: string;
        }
      );
    }
    if (typeof value === 'boolean') {
      return renderBoolean(key, value);
    }
    if (typeof value === 'string') {
      return renderString(key, specs);
    }
    if (typeof value === 'number') {
      return renderNumber(key, value);
    }
    return null;
  };

  const printerSpecification = getSpecifications();

  return (
    <FeatureContainer>
      {Object.entries(printerSpecification).map(([key, value]) =>
        renderSpecificationItem(key, value, printerSpecification)
      )}
    </FeatureContainer>
  );
};
